import React, { useState } from "react";
import { Users, CalendarDays, ClipboardCheck, Plus, Trash2, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { reuniones as api } from "../../api/modulos.service";
import { estilos as est, Cabecera, Vacio, Aviso, Ventana, Entrada, Area, Seleccion, useCarga } from "./base";

/**
 * JUNTA DIRECTIVA: convocatoria, acta y acuerdos.
 *
 * Las tres cosas en una sola entidad porque son tres momentos de lo mismo. La
 * convocatoria se escribe antes; el acta y los acuerdos, después, sobre la
 * misma reunión. Partirlo en dos módulos obliga a cruzarlos a mano y acaba
 * habiendo actas sin convocatoria.
 *
 * Los acuerdos llevan responsable y fecha. Un acuerdo sin responsable es una
 * frase bonita en un documento que nadie vuelve a abrir.
 */

const FORM_VACIO = { title: "", date: "", place: "", agenda: "" };
const ACUERDO_VACIO = { text: "", responsible: "", dueDate: "", status: "pendiente" };

const fecha = (f) =>
  new Date(f).toLocaleString("es-SV", { timeZone: "America/El_Salvador", dateStyle: "medium", timeStyle: "short" });

const COLOR_ACUERDO = { pendiente: "var(--warning)", cumplido: "var(--success)", descartado: "var(--text-tertiary)" };

export default function Junta({ rol }) {
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [acta, setActa] = useState(null); // la reunión cuya acta se edita
  const [texto, setTexto] = useState("");
  const [acuerdos, setAcuerdos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => api.listar({}), []);
  const lista = datos?.meetings || [];
  const esAdmin = rol === "admin";

  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const convocar = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      await api.crear(form);
      setAbierta(false);
      setForm(FORM_VACIO);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo convocar");
    } finally {
      setGuardando(false);
    }
  };

  const abrirActa = (r) => {
    setActa(r);
    setTexto(r.minutes || "");
    setAcuerdos(
      (r.resolutions || []).map((a) => ({
        text: a.text || "",
        responsible: a.responsible || "",
        dueDate: a.dueDate ? String(a.dueDate).slice(0, 10) : "",
        status: a.status || "pendiente",
      }))
    );
  };

  const guardarActa = async () => {
    setError("");
    setGuardando(true);
    try {
      await api.levantarActa(acta._id, {
        minutes: texto,
        resolutions: acuerdos.filter((a) => a.text.trim()).map((a) => ({ ...a, dueDate: a.dueDate || null })),
        status: "celebrada",
      });
      setActa(null);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo guardar el acta");
    } finally {
      setGuardando(false);
    }
  };

  const cambiarAcuerdo = (i, campo) => (e) =>
    setAcuerdos(acuerdos.map((a, j) => (j === i ? { ...a, [campo]: e.target.value } : a)));

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Junta directiva"
        subtitulo="Convocatorias, actas y acuerdos con responsable."
        accion={esAdmin ? "Convocar reunión" : null}
        alAccionar={() => setAbierta(true)}
        icono={CalendarDays}
      />

      {error && <Aviso>{error}</Aviso>}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : lista.length === 0 ? (
        <Vacio>No hay reuniones registradas.</Vacio>
      ) : (
        <div style={{ display: "grid", gap: "var(--space-4)" }}>
          {lista.map((r) => (
            <div key={r._id} style={est.tarjeta}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                <Users size={18} style={{ color: "var(--gold)", flexShrink: 0 }} />
                <h3 style={est.tarjetaTitulo}>{r.title}</h3>
              </div>
              <div style={est.tarjetaDato}>
                {fecha(r.date)}
                {r.place ? ` · ${r.place}` : ""} · {r.status === "celebrada" ? "Celebrada" : r.status === "cancelada" ? "Cancelada" : "Convocada"}
              </div>
              {r.agenda && (
                <div style={{ ...est.tarjetaDato, display: "block", whiteSpace: "pre-wrap" }}>{r.agenda}</div>
              )}

              {r.minutes && (
                <div style={{ ...est.tarjetaDato, display: "block", whiteSpace: "pre-wrap", marginTop: "var(--space-2)" }}>
                  <strong style={{ color: "var(--text)" }}>Acta: </strong>
                  {r.minutes}
                </div>
              )}

              {(r.resolutions || []).length > 0 && (
                <div style={{ display: "grid", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
                  {r.resolutions.map((a, i) => (
                    <div
                      key={i}
                      style={{
                        ...est.tarjetaDato,
                        display: "block",
                        paddingLeft: "var(--space-3)",
                        borderLeft: `3px solid ${COLOR_ACUERDO[a.status] || "var(--border)"}`,
                      }}
                    >
                      {a.text}
                      <div style={{ fontSize: "var(--text-xs)", color: "var(--text-tertiary)" }}>
                        {a.responsible ? `Responsable: ${a.responsible}` : "Sin responsable"}
                        {a.dueDate ? ` · para el ${String(a.dueDate).slice(0, 10)}` : ""} · {a.status}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {esAdmin && (
                <div style={est.tarjetaAcciones}>
                  <Button size="sm" variant="outline" leftIcon={ClipboardCheck} onClick={() => abrirActa(r)}>
                    {r.minutes ? "Editar acta" : "Levantar acta"}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo="Convocar una reunión"
        abierta={abierta}
        alCerrar={() => setAbierta(false)}
        pie={
          <>
            <Button variant="ghost" onClick={() => setAbierta(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={convocar} loading={guardando}>
              Convocar
            </Button>
          </>
        }
      >
        {error && <Aviso>{error}</Aviso>}
        <Entrada etiqueta="Título" value={form.title} onChange={cambiar("title")} required />
        <div style={est.fila}>
          <Entrada etiqueta="Fecha y hora" type="datetime-local" value={form.date} onChange={cambiar("date")} required />
          <Entrada etiqueta="Lugar" value={form.place} onChange={cambiar("place")} placeholder="Casa club" />
        </div>
        <Area etiqueta="Agenda" value={form.agenda} onChange={cambiar("agenda")} placeholder={"1. Cuota de mantenimiento\n2. Portón norte"} />
      </Ventana>

      <Ventana
        titulo={acta ? `Acta de «${acta.title}»` : ""}
        abierta={Boolean(acta)}
        alCerrar={() => setActa(null)}
        pie={
          <>
            <Button variant="ghost" onClick={() => setActa(null)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={guardarActa} loading={guardando}>
              Guardar acta
            </Button>
          </>
        }
      >
        {error && <Aviso>{error}</Aviso>}
        <Area etiqueta="Lo que se trató" value={texto} onChange={(e) => setTexto(e.target.value)} />

        <span style={{ ...est.etiqueta, marginTop: "var(--space-3)" }}>Acuerdos</span>
        {acuerdos.map((a, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gap: "var(--space-2)",
              padding: "var(--space-3)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
            }}
          >
            <Entrada etiqueta={`Acuerdo ${i + 1}`} value={a.text} onChange={cambiarAcuerdo(i, "text")} />
            <div style={est.fila}>
              <Entrada etiqueta="Responsable" value={a.responsible} onChange={cambiarAcuerdo(i, "responsible")} />
              <Entrada etiqueta="Para el" type="date" value={a.dueDate} onChange={cambiarAcuerdo(i, "dueDate")} />
            </div>
            <div style={est.fila}>
              <Seleccion
                etiqueta="Estado"
                value={a.status}
                onChange={cambiarAcuerdo(i, "status")}
                opciones={[
                  { valor: "pendiente", etiqueta: "Pendiente" },
                  { valor: "cumplido", etiqueta: "Cumplido" },
                  { valor: "descartado", etiqueta: "Descartado" },
                ]}
              />
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={Trash2}
                  onClick={() => setAcuerdos(acuerdos.filter((_, j) => j !== i))}
                >
                  Quitar
                </Button>
              </div>
            </div>
          </div>
        ))}
        <Button size="sm" variant="outline" leftIcon={Plus} onClick={() => setAcuerdos([...acuerdos, { ...ACUERDO_VACIO }])}>
          Agregar acuerdo
        </Button>
      </Ventana>
    </div>
  );
}
